
import { useState } from 'react';
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import { useNavigate } from 'react-router-dom';
import { personalInfoSchema } from '@/components/form-sections/PersonalInfoSection';
import { addressSchema } from '@/components/form-sections/AddressSection';
import { additionalInfoSchema } from '@/components/form-sections/AdditionalInfoSection';
import { invoiceDataSchema } from '@/components/form-sections/InvoiceDataSection';
import { usePaymentProcessing } from '@/hooks/usePaymentProcessing';

// Define the combined form schema based on installation type
const getFormSchema = (installationType: 'home' | 'company') => {
  const baseSchema = {
    ...personalInfoSchema.shape,
    ...addressSchema.shape,
    ...additionalInfoSchema.shape,
  };

  // We always include invoice data fields, but for company we add companyName and nip
  const invoiceFields = installationType === 'company' 
    ? invoiceDataSchema.shape 
    : {
        invoiceStreet: invoiceDataSchema.shape.invoiceStreet,
        invoiceBuildingNumber: invoiceDataSchema.shape.invoiceBuildingNumber,
        invoiceApartmentNumber: invoiceDataSchema.shape.invoiceApartmentNumber,
        invoiceCity: invoiceDataSchema.shape.invoiceCity,
        invoicePostalCode: invoiceDataSchema.shape.invoicePostalCode,
        usePersonalDataForInvoice: invoiceDataSchema.shape.usePersonalDataForInvoice,
      };

  return z.object({
    ...baseSchema,
    ...invoiceFields,
  });
};

interface UseClientFormProps {
  selectedOffer: 'standard' | 'premium' | 'exclusive';
  offerPrice: number;
  onSubmitSuccess: () => void;
  numberOfRooms: number;
  roomAreas: number[];
  installationType: 'home' | 'company';
}

export const useClientForm = ({ 
  selectedOffer, 
  offerPrice,
  onSubmitSuccess,
  numberOfRooms,
  roomAreas,
  installationType
}: UseClientFormProps) => {
  const navigate = useNavigate();
  const [showPaymentBreakdown, setShowPaymentBreakdown] = useState(false);
  const [formData, setFormData] = useState<any | null>(null);
  
  // Calculate total square area from room areas
  const totalSquareArea = roomAreas.reduce((sum, area) => sum + area, 0);
  
  console.log('useClientForm - values:', { numberOfRooms, roomAreas, totalSquareArea });
  
  const {
    isSubmitting,
    processingStep,
    progressValue,
    processingSteps,
    processPaymentData
  } = usePaymentProcessing({ 
    selectedOffer, 
    offerPrice, 
    onSubmitSuccess,
    numberOfRooms,
    roomAreas,
    totalSquareArea
  });

  // Get the appropriate schema based on installation type
  const formSchema = getFormSchema(installationType);
  type FormValues = z.infer<typeof formSchema>;
  
  // Define default values based on installation type
  const getDefaultValues = () => {
    const baseDefaults = {
      firstName: "Jan",
      lastName: "Kowalski",
      email: "jan.kowalski@example.com",
      phone: "500600700",
      street: "Marszałkowska",
      buildingNumber: "42",
      apartmentNumber: "15",
      city: "Warszawa",
      postalCode: "00-123",
      additionalInfo: "Preferowany montaż w godzinach popołudniowych, po 16:00.",
      policyAccepted: false,
      // Add invoice fields for both types
      invoiceStreet: "",
      invoiceBuildingNumber: "",
      invoiceApartmentNumber: "",
      invoiceCity: "",
      invoicePostalCode: "",
      usePersonalDataForInvoice: false,
    };

    if (installationType === 'company') {
      return {
        ...baseDefaults,
        companyName: "",
        nip: "",
      };
    }

    return baseDefaults;
  };
  
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: getDefaultValues(),
  });

  const handleUsePersonalData = () => {
    const { firstName, lastName, street, buildingNumber, apartmentNumber, city, postalCode } = form.getValues();
    
    // We need to typecast here to access the fields
    const formWithInvoice = form as any;
    
    // For company type, also set the company name
    if (installationType === 'company') {
      formWithInvoice.setValue('companyName', `${firstName} ${lastName}`, { shouldValidate: true });
    }
    
    // Set address fields for both types
    formWithInvoice.setValue('invoiceStreet', street, { shouldValidate: true });
    formWithInvoice.setValue('invoiceBuildingNumber', buildingNumber, { shouldValidate: true });
    formWithInvoice.setValue('invoiceApartmentNumber', apartmentNumber || "", { shouldValidate: true });
    formWithInvoice.setValue('invoiceCity', city, { shouldValidate: true });
    formWithInvoice.setValue('invoicePostalCode', postalCode, { shouldValidate: true });
  };

  const onSubmit = async (data: FormValues) => {
    try {
      console.log('onSubmit - form data with room details:', { ...data, numberOfRooms, totalSquareArea });
      setFormData(data);
      setShowPaymentBreakdown(true);
    } catch (error) {
      console.error("Błąd formularza:", error);
      toast.error("Błąd formularza", {
        description: "Wystąpił problem z formularzem. Sprawdź poprawność danych.",
      });
    }
  };

  const handlePaymentConfirm = () => {
    setShowPaymentBreakdown(false);
    if (formData) {
      processPaymentData(formData);
    }
  };

  // Safe way to access the usePersonalDataForInvoice field
  const isUsePersonalDataChecked = Boolean((form as any).watch('usePersonalDataForInvoice'));

  return {
    form,
    showPaymentBreakdown,
    setShowPaymentBreakdown,
    isSubmitting,
    processingStep,
    progressValue,
    processingSteps,
    handleUsePersonalData,
    onSubmit: form.handleSubmit(onSubmit),
    handlePaymentConfirm,
    isUsePersonalDataChecked
  };
};
